import { Table } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import BlockComponent from "../../../components/block/block";
import { TableCell } from "../../../components/table/table-cell/table-cell";

/** A group's tally for its row: how many of its permissions the role holds. */
export const groupTally = (permissions, held) =>
  `${permissions.filter((permission) => held.has(permission.key)).length} of ${permissions.length} on`;

/**
 * LF360-2045: a role's permissions, grouped as in the reference.
 *
 * `renderAllowed(permission, held)` draws the last column — a tick here; the
 * edit form passes a switch.
 *
 * The table looks like every other one: the shared `lf-table` in a block. A
 * group is a row of its own: its label, and in the Allowed column how many of
 * its permissions are on, which follows the switches while editing.
 */
const RolePermissions = ({ catalog, permissions = [], renderAllowed }) => {
  if (!catalog) return null;
  const held = new Set(permissions);

  const rows = catalog.groups.flatMap((group) => {
    const inGroup = catalog.permissions.filter((permission) => permission.group === group.key);
    return [
      { key: `group:${group.key}`, group: group.label, tally: groupTally(inGroup, held) },
      ...inGroup.map((permission) => ({ key: permission.key, permission })),
    ];
  });

  const columns = [
    {
      title: "Permission",
      key: "permission",
      render: (_, row) => (row.group ? <TableCell title={row.group} /> : row.permission.label),
    },
    {
      title: "Allowed",
      key: "allowed",
      width: 120,
      align: "center",
      render: (_, row) => {
        if (row.group) return <span className="lf-table-cell__text">{row.tally}</span>;
        const isHeld = held.has(row.permission.key);
        if (renderAllowed) return renderAllowed(row.permission, isHeld);
        return isHeld
          ? <CheckOutlined aria-label="Allowed" />
          : <span aria-label="Not allowed">—</span>;
      },
    },
  ];

  return (
    <BlockComponent>
      <Table
        className="lf-table"
        columns={columns}
        dataSource={rows}
        pagination={false}
        rowKey="key"
        onRow={(row) => (row.permission ? { "data-permission": row.permission.key } : {})}
      />
    </BlockComponent>
  );
};

export default RolePermissions;
